'use client'

import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  use,
  useContext,
  useMemo,
} from 'react'
import { useLocalStorage, useSessionStorage } from 'usehooks-ts'

import { extractUsernameFromEmail } from '@/lib/auth'
import { AppConfig } from '@/types/configs'
import { GUEST_SESSION_STORE_KEY, GuestSessionStore } from '@/types/session-store'
import { HUBS_STORE_LOCAL_KEY, HubsStoreState } from '@/types/store'

type ConfigsContextType = {
  store: HubsStoreState | null
  setStore: Dispatch<SetStateAction<HubsStoreState>>
  sessionStore: GuestSessionStore | null
  setSessionStore: Dispatch<SetStateAction<GuestSessionStore>>
  removeStore: () => void
  appConfig: AppConfig
  accountType: 'wallet_account' | 'htc_account' | 'guest'
  accountId: string
}

const ConfigsContext = createContext<ConfigsContextType | null>(null)

type ConfigsProviderProps = PropsWithChildren<{
  appConfigPromise: Promise<AppConfig>
}>

export const ConfigsProvider = ({ children, appConfigPromise }: ConfigsProviderProps) => {
  const appConfig = use(appConfigPromise)

  const [store, setStore, removeStore] = useLocalStorage<HubsStoreState>(HUBS_STORE_LOCAL_KEY, {})
  const [sessionStore, setSessionStore] = useSessionStorage<GuestSessionStore>(
    GUEST_SESSION_STORE_KEY,
    {},
  )

  const credentials = store?.credentials
  const viveportAuth = store?.viveportAuth
  const email = credentials?.email
  const walletAddress = viveportAuth?.walletAddress
  const accountEmail = viveportAuth?.accountEmail
  const accountPhoneNumber = viveportAuth?.accountPhoneNumber

  const accountId = useMemo(() => extractUsernameFromEmail(email!), [email])

  const accountType = useMemo(() => {
    if (walletAddress) {
      return 'wallet_account'
    }
    if (accountEmail || accountPhoneNumber) {
      return 'htc_account'
    }
    return 'guest'
  }, [walletAddress, accountEmail, accountPhoneNumber])

  return (
    //* we use this way to ensure that the children are rendered only after the appConfig is loaded
    <ConfigsContext.Provider
      value={{
        store,
        setStore,
        removeStore,
        sessionStore,
        setSessionStore,
        appConfig,
        accountType,
        accountId,
      }}
    >
      {children}
    </ConfigsContext.Provider>
  )
}

export const useConfigsContext = () => {
  const value = useContext(ConfigsContext)

  if (value == null) {
    throw new Error('useConfigsContext cannot be used outside of ConfigsProvider')
  }

  return value
}
